// VerifyPaymentForm — the admin's per-row action for a payment awaiting money confirmation.
// With a student code: the admin types the code from their own M-Pesa statement and the server
// compares it (PATCH /verify). Without one: a confirmed manual override. All matching lives on the server.
import { useState } from "react";
import { useVerifyPayment } from "../../hooks/useVerifyPayment.js";
import MarkPaidControl from "./MarkPaidControl.js";
import { normaliseProofReference } from "@dm-lms/shared/utils";
import { PAYMENT_REFERENCE_LENGTH } from "@dm-lms/shared/constants";
import type { AdminPayment, PaymentWithProof } from "@dm-lms/shared/types";

interface VerifyPaymentFormProps {
  payment:   Pick<AdminPayment, "id" | "code" | "amountKes" | "reference" | "status">;
  onSettled: (payment: PaymentWithProof) => void;   // the updated row, after a match or a mismatch
}

export default function VerifyPaymentForm({ payment, onSettled }: VerifyPaymentFormProps) {
  const [input, setInput] = useState("");
  const { isSubmitting, error, verify: submit } = useVerifyPayment(payment.id, onSettled);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  async function verify(adminCode: string) {
    const result = await submit({ adminCode });
    if (result?.match) setConfirmation("✓ Payment verified — codes matched");
  }

  if (confirmation) {
    return (
      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
        {confirmation}
      </div>
    );
  }

  // Only unpaid payments have anything to verify (failed ones can be re-checked after a mismatch)
  if (payment.status !== "pending" && payment.status !== "failed") {
    return <span className="text-dm-slate">—</span>;
  }

  if (payment.reference === null) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-dm-slate">Awaiting student code</p>
        <MarkPaidControl
          payment={payment}
          buttonLabel="Mark paid manually"
          buttonClass="rounded-md border border-green-600 px-3 py-1 text-xs font-medium text-green-700 transition hover:bg-green-50"
          onPaid={(settled) => {
            onSettled(settled);
            setConfirmation("✓ Payment confirmed — proof recorded");
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-dm-slate">
        Student code: <strong className="font-mono text-sm text-dm-navy">{payment.reference}</strong>
      </p>
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void verify(input);
        }}
      >
        <input
          type="text"
          value={input}
          maxLength={PAYMENT_REFERENCE_LENGTH.max}
          placeholder="Enter code from your M-Pesa statement"
          aria-label={`M-Pesa statement code for ${payment.code}`}
          onChange={(event) => { setInput(normaliseProofReference(event.target.value)); }}
          className="w-40 sm:w-56 rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
        />
        <button
          type="submit"
          disabled={input.length < PAYMENT_REFERENCE_LENGTH.min || isSubmitting}
          className="rounded-md bg-dm-navy px-4 py-1.5 text-sm font-medium text-white transition hover:bg-dm-blue disabled:opacity-50"
        >
          {isSubmitting ? "Verifying…" : "Verify"}
        </button>
      </form>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
